/* eslint-disable new-cap */
/* eslint-disable valid-jsdoc */
/* eslint-disable max-len */
/**
 * MSAHeader.ts - Interactive MSA Header Component
 * This module provides functionality for an interactive MSA (Multiple Sequence Alignment) header
 * with position markers, slider navigation, and position selection.
 */
import * as ui from 'datagrok-api/ui';
import * as DG from 'datagrok-api/dg';
import * as grok from 'datagrok-api/grok';
import { HelmTypes } from '../helm/consts';
import { buildCompositionTable } from './composition-table';
// WebLogo Constants
const WEBLOGO_CONSTANTS = {
    PADDING: 2,
    MIN_LETTER_HEIGHT: 4,
    SEPARATOR_WIDTH: 1,
    MIN_GRID_COLUMNS: 45
};
// Typography Constants
const FONTS = {
    TITLE: 'bold 10px Roboto, Roboto Local',
    COLUMN_TITLE: 'bold 13px Roboto, Roboto Local',
    POSITION_LABELS: '12px monospace',
    CONSERVATION_TEXT: '9px monospace',
    TOOLTIP_MAIN: '13px',
    TOOLTIP_SECONDARY: '12px',
    TOOLTIP_SMALL: '11px',
};
// Color Constants
const COLORS = {
    TITLE_TEXT: '#333333',
    SELECTION_HIGHLIGHT: 'rgba(60, 177, 115, 0.1)',
    SELECTION_STRONG: 'rgba(60, 177, 115, 0.2)',
    SELECTION_CONNECTION: 'rgba(60, 177, 115, 0.4)',
    BACKGROUND_LIGHT: 'rgba(240, 240, 240, 0.5)',
    BORDER_LIGHT: 'rgba(100, 100, 100, 0.3)',
    SEPARATOR_LIGHT: 'rgba(255, 255, 255, 0.4)',
    HOVER_SHADOW: 'rgba(255, 255, 255, 0.8)',
    SLIDER_DEFAULT: 'rgba(220, 220, 220, 0.4)',
    SLIDER_WINDOW: 'rgba(150, 150, 150, 0.5)',
    POSITION_DOT: '#999999',
    SLIDER_MARKER: '#3CB173',
};
// Tooltip Style Template
const TOOLTIP_STYLE = {
    color: '#4A4A49',
    background: '#FFFBCC',
    borderRadius: '3px',
    padding: '8px',
    fontSize: '12px',
    fontFamily: 'Roboto, Roboto Local',
    minWidth: '120px'
};
/**
 * Base class for all MSA header tracks
 */
export class MSAHeaderTrack {
    constructor(height = LAYOUT_CONSTANTS.DEFAULT_TRACK_HEIGHT, minHeight = LAYOUT_CONSTANTS.MIN_TRACK_HEIGHT, title = '') {
        this.ctx = null;
        this.visible = true;
        this.title = '';
        this.tooltipEnabled = false;
        this.tooltipContent = null;
        this.height = height;
        this.defaultHeight = height;
        this.minHeight = minHeight;
        this.title = title;
    }
    /**
     * Initialize the track with a canvas context
     */
    init(ctx) {
        this.ctx = ctx;
    }
    getMonomerAt(_x, _y, _position) {
        return null;
    }
    enableTooltip(enabled) {
        this.tooltipEnabled = enabled;
    }
    setTooltipContentGenerator(contentGenerator) {
        this.tooltipContent = contentGenerator;
    }
    getTooltipContent(position, monomer) {
        if (!this.tooltipEnabled || !this.tooltipContent)
            return null;
        const positionData = this.getPositionData(position) ?? new Map();
        return this.tooltipContent(position, monomer, positionData);
    }
    /**
     * Get data for a specific position (to be implemented by subclasses)
     */
    getPositionData(position) {
        return null;
    }
    setVisible(visible) {
        this.visible = visible;
    }
    getHeight() {
        return this.visible ? this.height : 0;
    }
    getDefaultHeight() {
        return this.defaultHeight;
    }
    getMinHeight() {
        return this.minHeight;
    }
    setHeight(height) {
        this.height = Math.max(this.minHeight, height);
    }
    resetHeight() {
        this.height = this.defaultHeight;
    }
    isVisible() {
        return this.visible;
    }
    setTitle(title) {
        this.title = title;
    }
    getTitle() {
        return this.title;
    }
}
export class WebLogoTrack extends MSAHeaderTrack {
    constructor(data = new Map(), height = LAYOUT_CONSTANTS.DEFAULT_TRACK_HEIGHT, _colorScheme = '', title = 'WebLogo') {
        super(height, LAYOUT_CONSTANTS.DEFAULT_TRACK_HEIGHT, title);
        this.data = new Map();
        this.monomerLib = null;
        this.biotype = 'HELM_AA';
        this.hoveredPosition = -1;
        this.hoveredMonomer = null;
        this.data = data;
        this.visible = data.size > 0;
    }
    setHovered(position, monomer) {
        this.hoveredPosition = position;
        this.hoveredMonomer = monomer;
    }
    getPositionData(position) {
        return this.data.get(position) || null;
    }
    setupDefaultTooltip() {
        this.enableTooltip(true);
        this.setTooltipContentGenerator((position, monomer, data) => {
            return this.createTooltipContent(position, monomer, data);
        });
    }
    createFrequencyTable(data) {
        let helmType;
        switch (this.biotype) {
            case 'HELM_BASE':
            case 'HELM_SUGAR':
            case HelmTypes.NUCLEOTIDE:
                helmType = HelmTypes.NUCLEOTIDE;
                break;
            case 'HELM_AA':
            case 'HELM_LINKER':
            default:
                helmType = HelmTypes.AA;
                break;
        }
        const totalFrequency = Array.from(data.values()).reduce((sum, freq) => sum + freq, 0);
        const displayTotal = 100;
        const counts = {};
        for (const [monomer, frequency] of data.entries())
            counts[monomer] = Math.max(1, Math.round((frequency / totalFrequency) * displayTotal));
        const table = buildCompositionTable(counts, helmType, this.monomerLib);
        table.style.fontSize = '11px';
        table.style.marginTop = '4px';
        return table;
    }
    createTooltipContent(position, monomer, data) {
        const tooltipRows = [];
        tooltipRows.push(ui.divText(`Position: ${position + 1}`, {
            style: { fontWeight: 'bold', marginBottom: '6px', fontSize: '13px' }
        }));
        if (monomer) {
            tooltipRows.push(ui.divText(`Monomer: ${monomer}`, {
                style: { marginBottom: '6px', fontSize: '13px' }
            }));
            if (data.has(monomer)) {
                tooltipRows.push(ui.divText(`${(data.get(monomer) * 100).toFixed(2)}%`, {
                    style: { marginBottom: '6px', fontSize: '13px' }
                }));
            }
        }
        if (data && data.size > 0) {
            // Use the datagrok buildCompositionTable
            const freqTable = this.createFrequencyTable(data);
            tooltipRows.push(freqTable);
        }
        else {
            tooltipRows.push(ui.divText('No data available', {
                style: { fontStyle: 'italic', color: '#666' }
            }));
        }
        const tooltipEl = ui.divV(tooltipRows);
        tooltipEl.style.maxHeight = '80vh';
        return tooltipEl;
    }
    setMonomerLib(monomerLib) {
        this.monomerLib = monomerLib;
    }
    setBiotype(biotype) {
        this.biotype = biotype;
    }
    /**
     * Calculate which monomer is at the specified coordinates within a WebLogo column
     */
    getMonomerAt(x, y, position) {
        if (!this.ctx || !this.visible || this.data.size === 0)
            return null;
        const residueFreqs = this.data.get(position);
        if (!residueFreqs || residueFreqs.size === 0)
            return null;
        // No title offset needed since we don't draw titles in tracks anymore
        const relativeY = y;
        const sortedResidues = Array.from(residueFreqs.entries()).sort((a, b) => b[1] - a[1]);
        const totalFreq = sortedResidues.reduce((sum, [_, freq]) => sum + freq, 0);
        const columnHeight = this.height;
        let currentY = 0;
        for (const [residue, freq] of sortedResidues) {
            const letterHeight = freq * columnHeight / totalFreq;
            if (relativeY >= currentY && relativeY < currentY + letterHeight)
                return residue;
            currentY += letterHeight;
        }
        return null;
    }
    updateData(data) {
        this.data = data;
        this.visible = data.size > 0;
    }
    /**
     * Draw the WebLogo track
     */
    draw(x, y, width, height, windowStart, positionWidth, totalPositions, currentPosition) {
        if (!this.ctx || !this.visible || this.data.size === 0)
            return;
        const visiblePositionsN = Math.floor(width / positionWidth);
        const effectiveLogoHeight = height - WEBLOGO_CONSTANTS.PADDING * 2;
        const columnY = y + WEBLOGO_CONSTANTS.PADDING;
        for (let i = 0; i < visiblePositionsN; i++) {
            this.drawWebLogoColumn(i, x, y, width, height, windowStart, positionWidth, totalPositions, currentPosition, columnY, effectiveLogoHeight);
        }
    }
    drawWebLogoColumn(i, x, y, width, height, windowStart, positionWidth, totalPositions, currentPosition, columnY, effectiveLogoHeight) {
        const position = windowStart + i - 1;
        if (position < 0 || position >= totalPositions)
            return;
        const residueFreqs = this.data.get(position);
        if (!residueFreqs || residueFreqs.size === 0)
            return;
        const posX = x + (i * positionWidth);
        const cellWidth = positionWidth - WEBLOGO_CONSTANTS.PADDING;
        // Highlight selected position
        if (windowStart + i === currentPosition) {
            this.ctx.fillStyle = COLORS.SELECTION_HIGHLIGHT;
            this.ctx.fillRect(posX, y, positionWidth, height);
        }
        // Draw column background
        this.ctx.fillStyle = COLORS.BACKGROUND_LIGHT;
        this.ctx.fillRect(posX + WEBLOGO_CONSTANTS.PADDING / 2, columnY, cellWidth, effectiveLogoHeight);
        this.drawLettersInColumn(position, posX, cellWidth, columnY, effectiveLogoHeight, residueFreqs);
        this.drawColumnBorder(posX, columnY, cellWidth, effectiveLogoHeight);
    }
    drawLettersInColumn(position, posX, cellWidth, columnY, effectiveLogoHeight, residueFreqs) {
        const sortedResidues = Array.from(residueFreqs.entries()).sort((a, b) => b[1] - a[1]);
        const totalFreq = sortedResidues.reduce((sum, [_, freq]) => sum + freq, 0);
        const scaleFactor = Math.min(1, effectiveLogoHeight / (totalFreq * effectiveLogoHeight));
        let currentY = columnY;
        const columnBottom = columnY + effectiveLogoHeight;
        for (const [residue, freq] of sortedResidues) {
            const rawLetterHeight = freq * effectiveLogoHeight * scaleFactor;
            const letterHeight = Math.max(WEBLOGO_CONSTANTS.MIN_LETTER_HEIGHT, Math.floor(rawLetterHeight));
            if (letterHeight < WEBLOGO_CONSTANTS.MIN_LETTER_HEIGHT)
                continue;
            const isHovered = position === this.hoveredPosition && residue === this.hoveredMonomer;
            const finalHeight = Math.min(letterHeight, columnBottom - currentY);
            if (finalHeight < WEBLOGO_CONSTANTS.MIN_LETTER_HEIGHT)
                break;
            this.drawLetter(residue, posX + WEBLOGO_CONSTANTS.PADDING / 2, currentY, cellWidth, finalHeight, isHovered);
            currentY += finalHeight;
        }
    }
    drawColumnBorder(posX, columnY, cellWidth, effectiveLogoHeight) {
        this.ctx.strokeStyle = COLORS.BORDER_LIGHT;
        this.ctx.lineWidth = 1;
        this.ctx.strokeRect(posX + WEBLOGO_CONSTANTS.PADDING / 2, columnY, cellWidth, effectiveLogoHeight);
    }
    drawLetter(letter, x, y, width, height, isHovered = false) {
        if (!this.ctx)
            return;
        const backgroundColor = this.getMonomerBackgroundColor(letter);
        const textColor = this.getMonomerTextColor(letter);
        // Fill background
        this.ctx.fillStyle = backgroundColor;
        this.ctx.fillRect(x, y, width, height);
        // Apply hover effect
        if (isHovered) {
            this.ctx.shadowColor = COLORS.HOVER_SHADOW;
            this.ctx.shadowBlur = 8;
            this.ctx.strokeStyle = 'white';
            this.ctx.lineWidth = 2;
            this.ctx.strokeRect(x, y, width, height);
            this.ctx.shadowBlur = 0;
        }
        this.drawLetterSeparators(x, y, width, height);
        this.drawLetterText(letter, x, y, width, height, textColor);
    }
    drawLetterSeparators(x, y, width, height) {
        this.ctx.strokeStyle = COLORS.SEPARATOR_LIGHT;
        this.ctx.lineWidth = WEBLOGO_CONSTANTS.SEPARATOR_WIDTH;
        if (y + height < y + this.ctx.canvas.height) {
            this.ctx.beginPath();
            this.ctx.moveTo(x, y + height);
            this.ctx.lineTo(x + width, y + height);
            this.ctx.stroke();
        }
    }
    drawLetterText(letter, x, y, width, height, textColor) {
        const fontSize = Math.min(height * 0.8, width * 0.8);
        if (fontSize >= 7) {
            this.ctx.fillStyle = textColor;
            this.ctx.font = `bold ${fontSize}px Roboto, Roboto Local`;
            this.ctx.textAlign = 'center';
            this.ctx.textBaseline = 'middle';
            this.ctx.fillText(letter, x + width / 2, y + height / 2);
        }
    }
    getMonomerBackgroundColor(letter) {
        if (this.monomerLib) {
            try {
                const colors = this.monomerLib.getMonomerColors(this.biotype, letter);
                if (colors && colors.backgroundcolor)
                    return colors.backgroundcolor;
            }
            catch (e) {
                console.warn('Error getting background color from monomerLib:', e);
            }
        }
        return '#CCCCCC';
    }
    getMonomerTextColor(letter) {
        const backgroundColor = this.getMonomerBackgroundColor(letter);
        try {
            const backgroundColorInt = DG.Color.fromHtml(backgroundColor);
            const contrastColorInt = DG.Color.getContrastColor(backgroundColorInt);
            return DG.Color.toHtml(contrastColorInt);
        }
        catch (e) {
            console.warn('Error calculating contrast color:', e);
            return '#000000';
        }
    }
}
/**
 * Track for displaying conservation bars
 */
export class ConservationTrack extends MSAHeaderTrack {
    constructor(data, height = LAYOUT_CONSTANTS.DEFAULT_TRACK_HEIGHT, colorScheme = 'default', title = 'Conservation') {
        super(height, LAYOUT_CONSTANTS.MIN_TRACK_HEIGHT, title);
        this.data = data;
        this.colorScheme = colorScheme;
        this.visible = data.length > 0;
    }
    updateData(data) {
        this.data = data;
        this.visible = data.length > 0;
    }
    /**
     * Draw the conservation track
     */
    draw(x, y, width, height, windowStart, positionWidth, totalPositions, currentPosition) {
        if (!this.ctx || !this.visible || this.data.length === 0)
            return;
        const visiblePositionsN = Math.floor(width / positionWidth);
        for (let i = 0; i < visiblePositionsN; i++) {
            const position = windowStart + i;
            if (position > totalPositions)
                break;
            const posX = x + (i * positionWidth);
            const cellWidth = positionWidth;
            const cellCenterX = posX + cellWidth / 2;
            if (position - 1 < this.data.length) {
                this.drawConservationBar(position - 1, posX, cellWidth, cellCenterX, y, height);
                if (position === currentPosition) {
                    this.ctx.fillStyle = COLORS.SELECTION_HIGHLIGHT;
                    this.ctx.fillRect(posX, y, cellWidth, height);
                }
            }
        }
    }
    drawConservationBar(posIndex, x, cellWidth, cellCenterX, barplotTop, barplotHeight) {
        if (!this.ctx)
            return;
        const conservation = this.data[posIndex];
        const barplotPadding = WEBLOGO_CONSTANTS.PADDING;
        // Draw bar background
        this.ctx.fillStyle = COLORS.BACKGROUND_LIGHT;
        this.ctx.fillRect(x + barplotPadding, barplotTop, cellWidth - barplotPadding * 2, barplotHeight);
        // Determine bar color based on color scheme
        let barColor = '#3CB173';
        if (this.colorScheme === 'default') {
            if (conservation < 0.5)
                barColor = '#E74C3C';
            else if (conservation < 0.75)
                barColor = '#F39C12';
        }
        else if (this.colorScheme === 'rainbow') {
            if (conservation < 0.2)
                barColor = '#E74C3C';
            else if (conservation < 0.4)
                barColor = '#FF7F00';
            else if (conservation < 0.6)
                barColor = '#FFFF00';
            else if (conservation < 0.8)
                barColor = '#00FF00';
            else
                barColor = '#0000FF';
        }
        else if (this.colorScheme === 'heatmap') {
            const intensity = Math.round(conservation * 255);
            barColor = `rgb(255, ${intensity}, ${intensity})`;
        }
        const barHeight = conservation * barplotHeight;
        this.ctx.fillStyle = barColor;
        this.ctx.fillRect(x + barplotPadding, barplotTop + barplotHeight - barHeight, cellWidth - barplotPadding * 2, barHeight);
        // Add outline
        this.ctx.strokeStyle = COLORS.BORDER_LIGHT;
        this.ctx.lineWidth = 1;
        this.ctx.strokeRect(x + barplotPadding, barplotTop, cellWidth - barplotPadding * 2, barplotHeight);
        // Add conservation percentage text if cell is wide enough
        if (cellWidth > 20) {
            this.ctx.fillStyle = COLORS.TITLE_TEXT;
            this.ctx.font = FONTS.CONSERVATION_TEXT;
            this.ctx.textAlign = 'center';
            this.ctx.textBaseline = 'middle';
            this.ctx.fillText(`${Math.round(conservation * 100)}%`, cellCenterX, barplotTop + barplotHeight / 2);
        }
    }
}
const LAYOUT_CONSTANTS = {
    TITLE_HEIGHT: 16,
    TRACK_GAP: 4,
    DOTTED_CELL_HEIGHT: 30,
    SLIDER_HEIGHT: 8,
    TOP_PADDING: 5,
    DEFAULT_TRACK_HEIGHT: 45,
    MIN_TRACK_HEIGHT: 35,
    ANNOTATION_TRACK_HEIGHT: 20,
};
// STRICT HEIGHT THRESHOLDS - All pixel-perfect and deterministic
const HEIGHT_THRESHOLDS = {
    // Base: just dotted cells + slider
    BASE: LAYOUT_CONSTANTS.DOTTED_CELL_HEIGHT + LAYOUT_CONSTANTS.SLIDER_HEIGHT, // 38px
    // With title but no tracks
    WITH_TITLE: function () {
        return this.BASE + LAYOUT_CONSTANTS.TITLE_HEIGHT + LAYOUT_CONSTANTS.TRACK_GAP; // 58px
    },
    // With title + WebLogo track
    WITH_WEBLOGO: function () {
        return this.WITH_TITLE() + LAYOUT_CONSTANTS.DEFAULT_TRACK_HEIGHT + LAYOUT_CONSTANTS.TRACK_GAP; // 107px
    },
    // With title + WebLogo + Conservation tracks
    WITH_BOTH: function () {
        return this.WITH_WEBLOGO() + LAYOUT_CONSTANTS.DEFAULT_TRACK_HEIGHT + LAYOUT_CONSTANTS.TRACK_GAP; // 156px
    }
};
export class MSAScrollingHeader {
    constructor(options, gridColumn) {
        this.gridColumn = gridColumn;
        this.canvas = null;
        this.ctx = null;
        this.tracks = new Map();
        // Tooltip and hover state
        this.currentHoverPosition = -1;
        this.currentHoverTrack = null;
        this.currentHoverMonomer = null;
        this.previousHoverPosition = -1;
        this.previousHoverTrack = null;
        this.previousHoverMonomer = null;
        // Selection state
        this.dataFrame = null;
        this.seqHandler = null;
        this.seqColumn = null;
        this.onSelectionCallback = null;
        // Track button state
        this.trackButtons = [];
        this.userSelectedTracks = null;
        this.config = {
            x: options.x || 0,
            y: options.y || 0,
            width: options.width || 0,
            height: options.height || 0,
            windowStartPosition: options.windowStartPosition || 1,
            positionWidth: options.positionWidth || 15,
            totalPositions: options.totalPositions || 5000,
            headerHeight: options.headerHeight || HEIGHT_THRESHOLDS.BASE,
            sliderHeight: options.sliderHeight || LAYOUT_CONSTANTS.SLIDER_HEIGHT,
            currentPosition: options.currentPosition || 1,
            cellBackground: options.cellBackground !== undefined ? options.cellBackground : true,
            sliderColor: options.sliderColor || COLORS.SLIDER_DEFAULT,
            onPositionChange: options.onPositionChange || ((_, __) => { }),
            onHeaderHeightChange: options.onHeaderHeightChange || (() => { }),
            ...options
        };
        this.eventElement = ui.div();
        this.eventElement.style.position = 'absolute';
        this.config.canvas.parentElement?.appendChild(this.eventElement);
        this.state = { isDragging: false, dragStartX: 0, dragMode: 'none', dragStartWindowPosition: 1, dragOccurred: false };
        this.setupEventListeners();
        this.init();
    }
    determineVisibleTracks() {
        const currentHeight = this.config.headerHeight;
        const webLogoTrack = this.getTrack('weblogo');
        const conservationTrack = this.getTrack('conservation');
        const annotationTrack = this.getTrack('annotations');
        // Reset all tracks
        this.tracks.forEach((track) => {
            track.setVisible(false);
            track.setHeight(LAYOUT_CONSTANTS.DEFAULT_TRACK_HEIGHT);
        });
        // Apply strict thresholds
        if (currentHeight < HEIGHT_THRESHOLDS.WITH_TITLE()) {
            // Below 58px: No tracks, no title
            return;
        }
        // Annotation track: compact (20px), shown whenever it exists and there's title space.
        // It was only added to the tracks map when it had regions, so existence implies data.
        let annotationSpace = 0;
        if (annotationTrack) {
            annotationTrack.setVisible(true);
            annotationTrack.setHeight(LAYOUT_CONSTANTS.ANNOTATION_TRACK_HEIGHT);
            annotationSpace = LAYOUT_CONSTANTS.ANNOTATION_TRACK_HEIGHT + LAYOUT_CONSTANTS.TRACK_GAP;
        }
        // Adjust thresholds for the space consumed by the annotation track
        const weblogoThreshold = HEIGHT_THRESHOLDS.WITH_WEBLOGO() + annotationSpace;
        const bothThreshold = HEIGHT_THRESHOLDS.WITH_BOTH() + annotationSpace;
        if (currentHeight < weblogoThreshold) {
            // Not enough room for WebLogo — annotation track only (if present)
            return;
        }
        if (currentHeight < bothThreshold) {
            // Room for WebLogo (+ annotations if present)
            if (webLogoTrack) {
                webLogoTrack.setVisible(true);
                const extraSpace = currentHeight - weblogoThreshold;
                webLogoTrack.setHeight(LAYOUT_CONSTANTS.DEFAULT_TRACK_HEIGHT + extraSpace);
            }
            return;
        }
        // Room for all tracks
        if (webLogoTrack)
            webLogoTrack.setVisible(true);
        if (conservationTrack)
            conservationTrack.setVisible(true);
        // Distribute extra space to WebLogo
        if (webLogoTrack && currentHeight > bothThreshold) {
            const extraSpace = currentHeight - bothThreshold;
            webLogoTrack.setHeight(LAYOUT_CONSTANTS.DEFAULT_TRACK_HEIGHT + extraSpace);
        }
        // Override with user selections if they exist (but still respect minimum heights)
        if (this.userSelectedTracks) {
            // Force hide tracks that user disabled
            this.tracks.forEach((track, id) => {
                if (!this.userSelectedTracks[id])
                    track.setVisible(false);
            });
            // But auto-hide if height is insufficient regardless of user preference
            if (currentHeight < weblogoThreshold && webLogoTrack)
                webLogoTrack.setVisible(false);
            if (currentHeight < bothThreshold && conservationTrack)
                conservationTrack.setVisible(false);
        }
    }
    drawTrackButtons() {
        const buttonWidth = 70;
        // todo: Make sure buttons are not colliding with the header
        if (!this.ctx || this.config.width < buttonWidth * 4)
            return;
        this.trackButtons = [];
        const conservationTrack = this.getTrack('conservation');
        const webLogoTrack = this.getTrack('weblogo');
        const annotationTrack = this.getTrack('annotations');
        const conservationVisible = conservationTrack?.isVisible() ?? false;
        const webLogoVisible = webLogoTrack?.isVisible() ?? false;
        const annotationVisible = annotationTrack?.isVisible() ?? false;
        const buttonHeight = 14;
        const buttonGap = 4;
        const rightMargin = 16;
        let buttonX = this.config.width - rightMargin;
        const buttonY = this.config.headerHeight >= HEIGHT_THRESHOLDS.WITH_TITLE() ?
            (LAYOUT_CONSTANTS.TITLE_HEIGHT - buttonHeight) / 2 : // Center in title area
            2; // Or just 2px from top if no title
        // Show buttons for hidden tracks so users can toggle them on
        const allMainVisible = conservationVisible && webLogoVisible;
        if (!allMainVisible) {
            if (!conservationVisible && conservationTrack) {
                buttonX -= buttonWidth;
                this.drawTrackButton('conservation', 'Conservation', buttonX, buttonY, buttonWidth, buttonHeight);
                buttonX -= buttonGap;
            }
            if (!webLogoVisible && webLogoTrack) {
                buttonX -= buttonWidth;
                this.drawTrackButton('weblogo', 'WebLogo', buttonX, buttonY, buttonWidth, buttonHeight);
                buttonX -= buttonGap;
            }
        }
        if (!annotationVisible && annotationTrack) {
            buttonX -= buttonWidth;
            this.drawTrackButton('annotations', 'Annotations', buttonX, buttonY, buttonWidth, buttonHeight);
        }
    }
    drawTrackButton(id, label, x, y, width, height, isAutoButton = false) {
        if (!this.ctx)
            return;
        this.trackButtons.push({ id, label, x, y, width, height });
        this.ctx.fillStyle = isAutoButton ? 'rgba(100, 150, 200, 0.8)' : 'rgba(240, 240, 240, 0.8)';
        this.ctx.fillRect(x, y, width, height);
        this.ctx.strokeStyle = isAutoButton ? 'rgba(70, 120, 170, 0.8)' : 'rgba(180, 180, 180, 0.8)';
        this.ctx.lineWidth = 1;
        this.ctx.strokeRect(x, y, width, height);
        this.ctx.fillStyle = isAutoButton ? '#ffffff' : '#666666';
        this.ctx.font = '9px Roboto, Roboto Local';
        this.ctx.textAlign = 'center';
        this.ctx.textBaseline = 'middle';
        this.ctx.fillText(label, x + width / 2, y + height / 2);
    }
    handleTrackButtonClick(x, y) {
        for (const button of this.trackButtons) {
            if (x >= button.x && x <= button.x + button.width &&
                y >= button.y && y <= button.y + button.height) {
                // if (button.id === 'auto')
                //   this.resetToAutoMode();
                // else
                //   this.snapToTrackHeight(button.id);
                // return true;
                this.snapToTrackHeight(button.id);
                return true;
            }
        }
        return false;
    }
    isInsideColumnHeaderArea(x, y) {
        const titleHeight = this.config.headerHeight >= HEIGHT_THRESHOLDS.WITH_TITLE() ? LAYOUT_CONSTANTS.TITLE_HEIGHT : 0;
        return y < titleHeight;
    }
    snapToTrackHeight(trackId) {
        // Calculate annotation space — the annotation track is always shown if present
        const annotationTrack = this.getTrack('annotations');
        const annotationSpace = annotationTrack
            ? LAYOUT_CONSTANTS.ANNOTATION_TRACK_HEIGHT + LAYOUT_CONSTANTS.TRACK_GAP : 0;
        let targetHeight;
        if (trackId === 'annotations') {
            // Just ensure title + annotation height
            targetHeight = HEIGHT_THRESHOLDS.WITH_TITLE() + annotationSpace;
        }
        else if (trackId === 'weblogo')
            targetHeight = HEIGHT_THRESHOLDS.WITH_WEBLOGO() + annotationSpace;
        else if (trackId === 'conservation')
            targetHeight = HEIGHT_THRESHOLDS.WITH_BOTH() + annotationSpace;
        else
            return;
        // Set user preference
        if (!this.userSelectedTracks) {
            this.userSelectedTracks = {};
            this.tracks.forEach((track, id) => {
                // Annotation track defaults to visible (compact, always-on); others start hidden
                this.userSelectedTracks[id] = id === 'annotations';
            });
        }
        // Enable the requested track and any dependencies
        if (trackId === 'annotations') {
            this.userSelectedTracks['annotations'] = true;
        }
        else if (trackId === 'conservation') {
            this.userSelectedTracks['weblogo'] = true; // Conservation requires WebLogo
            this.userSelectedTracks['conservation'] = true;
        }
        else if (trackId === 'weblogo') {
            this.userSelectedTracks['weblogo'] = true;
        }
        // Snap to exact height
        if (this.config.onHeaderHeightChange)
            this.config.onHeaderHeightChange(targetHeight);
        window.requestAnimationFrame(() => this.redraw());
    }
    resetToAutoMode() {
        this.userSelectedTracks = null;
        // Calculate automatic initial height, accounting for annotation track
        const annotationTrack = this.getTrack('annotations');
        const annotationSpace = annotationTrack
            ? LAYOUT_CONSTANTS.ANNOTATION_TRACK_HEIGHT + LAYOUT_CONSTANTS.TRACK_GAP : 0;
        const hasMultipleSequences = this.tracks.size > 0;
        const initialHeight = hasMultipleSequences
            ? HEIGHT_THRESHOLDS.WITH_BOTH() + annotationSpace : HEIGHT_THRESHOLDS.BASE;
        if (this.config.onHeaderHeightChange)
            this.config.onHeaderHeightChange(initialHeight);
        window.requestAnimationFrame(() => this.redraw());
    }
    /**
     * Draw the column title (shown when above threshold)
     */
    drawColumnTitle(x, y, width, columnName) {
        if (!this.ctx || !columnName)
            return;
        if (this.config.headerHeight >= HEIGHT_THRESHOLDS.WITH_TITLE()) {
            this.ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
            this.ctx.fillRect(x, y, width, LAYOUT_CONSTANTS.TITLE_HEIGHT);
            this.ctx.fillStyle = COLORS.TITLE_TEXT;
            this.ctx.font = FONTS.COLUMN_TITLE;
            this.ctx.textAlign = 'center';
            this.ctx.textBaseline = 'middle';
            this.ctx.fillText(columnName, x + width / 2, y + LAYOUT_CONSTANTS.TITLE_HEIGHT / 2);
        }
    }
    clearHoverStates() {
        const hasHoverState = this.previousHoverPosition !== -1 ||
            this.previousHoverTrack !== null ||
            this.previousHoverMonomer !== null;
        if (hasHoverState) {
            this.previousHoverPosition = -1;
            this.previousHoverTrack = null;
            this.previousHoverMonomer = null;
            this.tracks.forEach((track) => {
                if (track instanceof WebLogoTrack)
                    track.setHovered(-1, null);
            });
            window.requestAnimationFrame(() => this.redraw());
        }
    }
    redraw() {
        this.draw(this.config.x, this.config.y, this.config.width, this.config.height, this.config.currentPosition, this.config.windowStartPosition, { preventDefault: () => { } }, this.seqColumn?.name);
    }
    setSelectionData(dataFrame, seqColumn, seqHandler, callback) {
        this.dataFrame = dataFrame;
        this.seqColumn = seqColumn;
        this.seqHandler = seqHandler;
        this.onSelectionCallback = callback || null;
    }
    setupTooltipHandling() {
        this.eventElement.addEventListener('mousemove', this.handleTooltipMouseMove.bind(this));
        this.eventElement.addEventListener('mouseleave', this.handleTooltipMouseLeave.bind(this));
    }
    handleTooltipMouseMove(e) {
        if (!this.isValid)
            return;
        const { x, y } = this.getCoords(e);
        const cellWidth = this.config.positionWidth;
        const hoveredCellIndex = Math.floor(x / cellWidth);
        const windowStart = this.config.windowStartPosition;
        const hoveredPosition = windowStart + hoveredCellIndex - 1;
        if (hoveredPosition < 0 || hoveredPosition >= this.config.totalPositions) {
            this.hideTooltip();
            this.clearHoverStates();
            return;
        }
        const titleHeight = this.config.headerHeight >= HEIGHT_THRESHOLDS.WITH_TITLE() ? LAYOUT_CONSTANTS.TITLE_HEIGHT : 0;
        const sliderTop = this.config.headerHeight - LAYOUT_CONSTANTS.SLIDER_HEIGHT;
        const dottedCellsTop = sliderTop - LAYOUT_CONSTANTS.DOTTED_CELL_HEIGHT;
        const tracksEndY = dottedCellsTop - LAYOUT_CONSTANTS.TRACK_GAP;
        if (y >= dottedCellsTop || y < titleHeight) {
            this.hideTooltip();
            this.clearHoverStates();
            return;
        }
        // Find hovered track working backwards from dotted cells (same order as drawing)
        let hoveredTrackId = null;
        let trackRelativeY = 0;
        const visibleTracks = [];
        this.tracks.forEach((track, id) => {
            if (track.isVisible())
                visibleTracks.push({ id, track });
        });
        let currentY = tracksEndY;
        for (const { id, track } of visibleTracks) {
            const trackHeight = track.getHeight();
            const trackStartY = currentY - trackHeight;
            if (y >= trackStartY && y < currentY) {
                hoveredTrackId = id;
                trackRelativeY = y - trackStartY;
                break;
            }
            currentY = trackStartY - LAYOUT_CONSTANTS.TRACK_GAP;
        }
        // Check for hovered monomer
        let currentHoverMonomer = null;
        if (hoveredTrackId) {
            const track = this.tracks.get(hoveredTrackId);
            if (track)
                currentHoverMonomer = track.getMonomerAt(x, trackRelativeY, hoveredPosition);
        }
        // Update hover state if changed
        const hoverStateChanged = (this.previousHoverPosition !== hoveredPosition ||
            this.previousHoverTrack !== hoveredTrackId ||
            this.previousHoverMonomer !== currentHoverMonomer);
        if (hoverStateChanged) {
            this.previousHoverPosition = hoveredPosition;
            this.previousHoverTrack = hoveredTrackId;
            this.previousHoverMonomer = currentHoverMonomer;
            if (hoveredTrackId) {
                const track = this.tracks.get(hoveredTrackId);
                if (track instanceof WebLogoTrack) {
                    track.setHovered(hoveredPosition, currentHoverMonomer);
                    window.requestAnimationFrame(() => this.redraw());
                }
            }
        }
        // Handle tooltips
        if (hoveredPosition !== this.currentHoverPosition || hoveredTrackId !== this.currentHoverTrack || currentHoverMonomer !== this.currentHoverMonomer) {
            this.currentHoverPosition = hoveredPosition;
            this.currentHoverTrack = hoveredTrackId;
            this.currentHoverMonomer = currentHoverMonomer;
            if (hoveredTrackId) {
                const track = this.tracks.get(hoveredTrackId);
                if (track) {
                    const tooltipContent = track.getTooltipContent(hoveredPosition, currentHoverMonomer);
                    if (tooltipContent) {
                        ui.tooltip.show(tooltipContent, e.clientX + 16, e.clientY + 16);
                        return;
                    }
                }
            }
        }
        if (!hoveredTrackId) {
            this.hideTooltip();
            this.clearHoverStates();
        }
    }
    handleTooltipMouseLeave() {
        this.hideTooltip();
        this.clearHoverStates();
        window.requestAnimationFrame(() => this.draw(this.config.x, this.config.y, this.config.width, this.config.height, this.config.currentPosition, this.config.windowStartPosition, { preventDefault: () => { } }, this.seqColumn?.name));
    }
    hideTooltip() {
        this.currentHoverPosition = -1;
        this.currentHoverTrack = null;
        ui.tooltip.hide();
    }
    draw(x, y, w, h, currentPos, scrollerStart, preventable, columnName) {
        Object.assign(this.config, {
            x, y, width: w, height: h,
            currentPosition: currentPos,
            windowStartPosition: scrollerStart
        });
        if (!this.isValid) {
            this.eventElement.style.display = 'none';
            return;
        }
        this.ctx.save();
        this.ctx.clearRect(x, y, w, h);
        this.ctx.translate(x, y);
        this.ctx.rect(0, 0, w, h);
        this.ctx.clip();
        this.determineVisibleTracks();
        const showTitle = this.config.headerHeight >= HEIGHT_THRESHOLDS.WITH_TITLE();
        const titleHeight = showTitle ? LAYOUT_CONSTANTS.TITLE_HEIGHT : 0;
        if (columnName && showTitle)
            this.drawColumnTitle(0, 0, w, columnName);
        const sliderTop = h - LAYOUT_CONSTANTS.SLIDER_HEIGHT;
        const dottedCellsTop = sliderTop - LAYOUT_CONSTANTS.DOTTED_CELL_HEIGHT;
        const tracksEndY = dottedCellsTop - LAYOUT_CONSTANTS.TRACK_GAP;
        const visibleTrackPositions = [];
        // Draw tracks working backwards from dotted cells (insertion order: annotations → weblogo → conservation)
        const visibleTracks = [];
        this.tracks.forEach((track, id) => {
            if (track.isVisible())
                visibleTracks.push({ id, track });
        });
        let currentY = tracksEndY;
        for (const { track } of visibleTracks) {
            const trackHeight = track.getHeight();
            const trackStartY = currentY - trackHeight;
            track.draw(0, trackStartY, w, trackHeight, this.config.windowStartPosition, this.config.positionWidth, this.config.totalPositions, this.config.currentPosition);
            visibleTrackPositions.unshift({ y: trackStartY, height: trackHeight });
            currentY = trackStartY - LAYOUT_CONSTANTS.TRACK_GAP;
        }
        // Draw dotted cells
        this.drawDottedCells(0, dottedCellsTop, w, LAYOUT_CONSTANTS.DOTTED_CELL_HEIGHT, sliderTop);
        visibleTrackPositions.push({ y: dottedCellsTop, height: LAYOUT_CONSTANTS.DOTTED_CELL_HEIGHT });
        // Draw connection lines for selected position
        if (this.config.currentPosition >= 1 && this.config.currentPosition <= this.config.totalPositions) {
            const cellWidth = this.config.positionWidth;
            const position = this.config.currentPosition;
            const windowStart = this.config.windowStartPosition;
            const visibleIndex = position - windowStart;
            if (visibleIndex >= 0 && visibleIndex < Math.floor(w / cellWidth)) {
                const posX = visibleIndex * cellWidth;
                const cellCenterX = posX + cellWidth / 2;
                for (let i = 0; i < visibleTrackPositions.length - 1; i++) {
                    const upperTrack = visibleTrackPositions[i];
                    const lowerTrack = visibleTrackPositions[i + 1];
                    this.ctx.strokeStyle = COLORS.SELECTION_CONNECTION;
                    this.ctx.lineWidth = 1;
                    this.ctx.beginPath();
                    this.ctx.moveTo(cellCenterX, upperTrack.y + upperTrack.height);
                    this.ctx.lineTo(cellCenterX, lowerTrack.y);
                    this.ctx.stroke();
                }
            }
        }
        this.drawTrackButtons();
        this.ctx.restore();
        preventable.preventDefault();
        this.setupEventElement();
    }
    /**
     * Draw the dotted cells area with position markers
     */
    drawDottedCells(x, y, width, height, sliderTop) {
        if (!this.ctx)
            return;
        const totalPositions = this.config.totalPositions;
        const positionWidth = this.config.positionWidth;
        const currentPosition = this.config.currentPosition;
        const windowStart = this.config.windowStartPosition;
        const visiblePositionsN = Math.floor(width / positionWidth);
        const posIndexTop = y + LAYOUT_CONSTANTS.TOP_PADDING;
        this.drawSlider(x, sliderTop, width);
        for (let i = 0; i < visiblePositionsN; i++) {
            const position = windowStart + i;
            if (position > totalPositions)
                break;
            const posX = x + (i * positionWidth);
            const cellWidth = positionWidth;
            const cellCenterX = posX + cellWidth / 2;
            if (this.config.cellBackground) {
                this.ctx.fillStyle = i % 2 === 0 ? 'rgba(248, 248, 248, 0.3)' : 'rgba(242, 242, 242, 0.2)';
                this.ctx.fillRect(posX, y, cellWidth, height);
                this.ctx.strokeStyle = 'rgba(220, 220, 220, 0.7)';
                this.ctx.beginPath();
                this.ctx.moveTo(posX, y);
                this.ctx.lineTo(posX, sliderTop);
                this.ctx.stroke();
            }
            // Draw position dot
            this.ctx.fillStyle = COLORS.POSITION_DOT;
            this.ctx.beginPath();
            this.ctx.arc(cellCenterX, posIndexTop + 5, 1, 0, Math.PI * 2);
            this.ctx.fill();
            // Draw position number
            if (position === currentPosition || ((position === 1 || position % 10 === 0) &&
                Math.abs(position - currentPosition) > 1)) {
                this.ctx.fillStyle = COLORS.TITLE_TEXT;
                this.ctx.font = FONTS.POSITION_LABELS;
                this.ctx.textAlign = 'center';
                this.ctx.textBaseline = 'middle';
                this.ctx.fillText(position.toString(), cellCenterX, posIndexTop + 15);
            }
            // Highlight current selected position
            if (position === currentPosition) {
                this.ctx.fillStyle = COLORS.SELECTION_STRONG;
                this.ctx.fillRect(posX, y, cellWidth, height);
            }
        }
    }
    /**
     * Draw the position slider
     */
    drawSlider(x, sliderTop, width) {
        if (!this.ctx)
            return;
        this.ctx.fillStyle = this.config.sliderColor;
        this.ctx.fillRect(x, sliderTop, width, LAYOUT_CONSTANTS.SLIDER_HEIGHT);
        const visiblePositionsN = Math.floor(width / this.config.positionWidth);
        const windowStart = this.config.windowStartPosition;
        const totalSliderRange = this.config.totalPositions - visiblePositionsN;
        const sliderWidth = this.sliderWidth;
        const sliderStartPX = totalSliderRange <= 0 ? 0 :
            (windowStart - 1) / totalSliderRange * (width - sliderWidth);
        const sliderLengthPX = totalSliderRange <= 0 ? width : sliderWidth;
        this.ctx.fillStyle = COLORS.SLIDER_WINDOW;
        this.ctx.fillRect(x + sliderStartPX, sliderTop, sliderLengthPX, LAYOUT_CONSTANTS.SLIDER_HEIGHT);
        if (this.config.currentPosition >= 1 && this.config.currentPosition <= this.config.totalPositions) {
            const currentPositionRatio = (this.config.currentPosition - 1) / (this.config.totalPositions - 1);
            const notchX = Math.round(currentPositionRatio * width);
            this.ctx.fillStyle = COLORS.SLIDER_MARKER;
            this.ctx.fillRect(x + notchX - 1, sliderTop - 2, 3, LAYOUT_CONSTANTS.SLIDER_HEIGHT + 4);
        }
    }
    setupEventElement() {
        this.eventElement.style.display = 'block';
        this.eventElement.style.left = `${this.config.x}px`;
        this.eventElement.style.top = `${this.config.y}px`;
        this.eventElement.style.width = `${this.config.width}px`;
        this.eventElement.style.height = `${this.config.height}px`;
    }
    getCoords(e) {
        const rect = this.canvas.getBoundingClientRect();
        const x = e.clientX - rect.left - this.config.x;
        const y = e.clientY - rect.top - this.config.y;
        return { x, y };
    }
    isInHeaderArea(e) {
        const { x, y } = this.getCoords(e);
        return x >= 0 && x <= this.config.width && y >= 0 && y <= this.config.headerHeight;
    }
    get positionWidth() {
        return this.config.positionWidth;
    }
    set positionWidth(value) {
        this.config.positionWidth = value;
    }
    isInSliderArea(e) {
        const { y } = this.getCoords(e);
        const sliderTop = this.config.headerHeight - LAYOUT_CONSTANTS.SLIDER_HEIGHT;
        return y > sliderTop && y < sliderTop + LAYOUT_CONSTANTS.SLIDER_HEIGHT;
    }
    get sliderWidth() {
        const pseudoPositionWidth = this.config.width / this.config.totalPositions;
        const w = pseudoPositionWidth * (this.config.width / this.config.positionWidth);
        return Math.max(w, 20);
    }
    isInSliderDraggableArea(e) {
        const { x, y } = this.getCoords(e);
        const sliderTop = this.config.headerHeight - LAYOUT_CONSTANTS.SLIDER_HEIGHT;
        const visiblePositionsN = Math.floor(this.config.width / this.config.positionWidth);
        const windowStart = this.config.windowStartPosition;
        const totalSliderRange = this.config.totalPositions - visiblePositionsN;
        const sliderStartPX = totalSliderRange <= 0 ? 0 :
            (windowStart - 1) / totalSliderRange * (this.config.width - this.sliderWidth);
        return y > sliderTop && y < sliderTop + LAYOUT_CONSTANTS.SLIDER_HEIGHT &&
            x >= sliderStartPX && x < sliderStartPX + this.sliderWidth;
    }
    setupEventListeners() {
        this.eventElement.addEventListener('mousemove', (e) => {
            if (!this.isValid)
                return;
            if (this.state.isDragging && this.state.dragMode === 'header')
                this.eventElement.style.cursor = 'grabbing';
            else if (this.isInSliderDraggableArea(e))
                this.eventElement.style.cursor = 'grab';
            else if (this.isInSliderArea(e))
                this.eventElement.style.cursor = 'pointer';
            else if (this.isInHeaderArea(e))
                this.eventElement.style.cursor = 'grab';
            else
                this.eventElement.style.cursor = 'default';
        });
        this.eventElement.addEventListener('mousedown', this.handleMouseDown.bind(this));
        this.eventElement.addEventListener('mousemove', this.handleMouseMove.bind(this));
        this.eventElement.addEventListener('mouseup', this.handleMouseUp.bind(this));
        this.eventElement.addEventListener('mouseleave', this.handleMouseUp.bind(this));
        this.eventElement.addEventListener('click', this.handleSelectionClick.bind(this));
        this.eventElement.addEventListener('click', this.handleClick.bind(this));
        this.eventElement.addEventListener('wheel', this.handleMouseWheel.bind(this));
        window.addEventListener('keydown', this.handleKeyDown.bind(this));
    }
    handleSelectionClick(e) {
        if (this.state.dragOccurred)
            return;
        if (!this.isValid || !this.dataFrame || !this.seqColumn || !this.seqHandler)
            return;
        const { x, y } = this.getCoords(e);
        if (this.handleTrackButtonClick(x, y))
            return;
        if (this.isInsideColumnHeaderArea(x, y))
            return;
        const cellWidth = this.config.positionWidth;
        const clickedCellIndex = Math.floor(x / cellWidth);
        const windowStart = this.config.windowStartPosition;
        const clickedPosition = windowStart + clickedCellIndex - 1;
        if (clickedPosition < 0 || clickedPosition >= this.config.totalPositions)
            return;
        const titleHeight = this.config.headerHeight >= HEIGHT_THRESHOLDS.WITH_TITLE() ? LAYOUT_CONSTANTS.TITLE_HEIGHT : 0;
        const sliderTop = this.config.headerHeight - LAYOUT_CONSTANTS.SLIDER_HEIGHT;
        const dottedCellsTop = sliderTop - LAYOUT_CONSTANTS.DOTTED_CELL_HEIGHT;
        const tracksEndY = dottedCellsTop - LAYOUT_CONSTANTS.TRACK_GAP;
        if (y >= dottedCellsTop || y < titleHeight)
            return;
        // Find clicked track
        let clickedTrackId = null;
        let trackRelativeY = 0;
        const visibleTracks = [];
        const webLogoTrack = this.getTrack('weblogo');
        if (webLogoTrack && webLogoTrack.isVisible())
            visibleTracks.push({ id: 'weblogo', track: webLogoTrack });
        const conservationTrack = this.getTrack('conservation');
        if (conservationTrack && conservationTrack.isVisible())
            visibleTracks.push({ id: 'conservation', track: conservationTrack });
        let currentY = tracksEndY;
        for (const { id, track } of visibleTracks) {
            const trackHeight = track.getHeight();
            const trackStartY = currentY - trackHeight;
            if (y >= trackStartY && y < currentY) {
                clickedTrackId = id;
                trackRelativeY = y - trackStartY;
                break;
            }
            currentY = trackStartY - LAYOUT_CONSTANTS.TRACK_GAP;
        }
        if (clickedTrackId) {
            const track = this.tracks.get(clickedTrackId);
            if (track) {
                const monomer = track.getMonomerAt(x, trackRelativeY, clickedPosition);
                if (monomer) {
                    if (this.onSelectionCallback) {
                        this.onSelectionCallback(clickedPosition, monomer);
                        return;
                    }
                    this.selectRowsWithMonomerAtPosition(clickedPosition, monomer);
                    e.stopPropagation();
                    e.stopImmediatePropagation();
                }
            }
        }
    }
    selectRowsWithMonomerAtPosition(position, monomer) {
        if (!this.dataFrame || !this.seqHandler)
            return;
        try {
            const selection = this.dataFrame.selection;
            const monomersAtPositon = this.seqHandler.getMonomersAtPosition(position, true);
            selection.init((i) => monomersAtPositon[i] === monomer);
        }
        catch (error) {
            console.error('Error selecting rows:', error);
        }
    }
    init() {
        this.canvas = this.config.canvas;
        if (!this.canvas) {
            console.error('Canvas not found');
            return;
        }
        const context = this.canvas.getContext('2d');
        if (!context) {
            console.error('Failed to get 2D context from canvas');
            return;
        }
        this.ctx = context;
        this.tracks.forEach((track) => track.init(context));
    }
    addTrack(id, track) {
        if (this.ctx)
            track.init(this.ctx);
        this.tracks.set(id, track);
    }
    removeTrack(id) {
        this.tracks.delete(id);
    }
    getTrack(id) {
        return this.tracks.get(id);
    }
    updateTrack(id, updater) {
        const track = this.getTrack(id);
        if (track)
            updater(track);
    }
    get isValid() {
        const gc = this.gridColumn;
        const g = gc?.grid;
        const minScroll = g?.horzScroll?.min || 0;
        const maxScroll = g?.horzScroll?.max || 1e7;
        return !!this.canvas && !!this.ctx &&
            this.config.height >= HEIGHT_THRESHOLDS.WITH_TITLE() &&
            g && (between(gc.left ?? 0, minScroll, maxScroll) || between(gc.right ?? Infinity, minScroll, maxScroll)); // check that the column is actually visible
    }
    handleMouseDown(e) {
        if (!this.isValid)
            return;
        const { x, y } = this.getCoords(e);
        if (this.isInSliderDraggableArea(e)) {
            this.state.isDragging = true;
            this.state.dragMode = 'slider';
            this.state.dragStartX = x;
            this.handleSliderDrag(x);
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
        }
        else if (this.isInHeaderArea(e) && !this.isInSliderArea(e)) {
            this.state.isDragging = true;
            this.state.dragMode = 'header';
            this.state.dragStartX = x;
            this.state.dragStartWindowPosition = this.config.windowStartPosition;
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
        }
    }
    handleMouseWheel(e) {
        if (!this.isValid)
            return;
        if (this.isInHeaderArea(e)) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            const delta = e.shiftKey ? Math.sign(e.deltaY) : Math.sign(e.deltaX || e.deltaY);
            const scrollSpeed = e.shiftKey ? 3 : 1;
            const newStartPosition = this.config.windowStartPosition + (delta * scrollSpeed);
            const visiblePositions = Math.floor(this.config.width / this.config.positionWidth);
            const maxStart = this.config.totalPositions - visiblePositions + 1;
            this.config.windowStartPosition = Math.max(1, Math.min(maxStart, newStartPosition));
            if (typeof this.config.onPositionChange === 'function')
                this.config.onPositionChange(this.config.currentPosition, this.getWindowRange());
        }
    }
    handleMouseMove(e) {
        if (!this.state.isDragging || !this.isValid)
            return;
        const rect = this.canvas.getBoundingClientRect();
        const x = e.clientX - rect.left - this.config.x;
        this.state.dragOccurred = true;
        if (this.state.dragMode === 'slider') {
            this.handleSliderDrag(x);
        }
        else if (this.state.dragMode === 'header') {
            const deltaX = x - this.state.dragStartX;
            const deltaPositions = Math.round(deltaX / this.config.positionWidth);
            const visiblePositions = Math.floor(this.config.width / this.config.positionWidth);
            const maxStart = this.config.totalPositions - visiblePositions + 1;
            this.config.windowStartPosition = Math.max(1, Math.min(maxStart, this.state.dragStartWindowPosition - deltaPositions));
            if (typeof this.config.onPositionChange === 'function')
                this.config.onPositionChange(this.config.currentPosition, this.getWindowRange());
        }
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
    }
    detach() {
        this.eventElement.remove(); // all event listeners are removed along with the element
        window.removeEventListener('keydown', this.handleKeyDown.bind(this));
    }
    handleKeyDown(e) {
        if (!this.isValid || this.config.currentPosition < 1)
            return;
        if (!document.activeElement?.contains(this.eventElement) || this.eventElement.style.display !== 'block')
            return;
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            const delta = e.key === 'ArrowLeft' ? -1 : 1;
            const newPosition = Math.min(Math.max(this.config.currentPosition + delta, 1), this.config.totalPositions);
            if (newPosition === this.config.currentPosition)
                return;
            this.config.currentPosition = newPosition;
            const visiblePositions = Math.floor(this.config.width / this.config.positionWidth);
            const start = this.config.windowStartPosition;
            const end = start + visiblePositions - 1;
            if (newPosition < start || newPosition > end) {
                if (delta < 0)
                    this.config.windowStartPosition = newPosition;
                else
                    this.config.windowStartPosition = Math.max(1, newPosition - visiblePositions + 1);
            }
        }
        else if (e.key === 'Escape') {
            this.config.currentPosition = -2;
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
        }
        else
            return;
        if (typeof this.config.onPositionChange === 'function')
            this.config.onPositionChange(this.config.currentPosition, this.getWindowRange());
    }
    handleMouseUp() {
        this.state.isDragging = false;
        this.state.dragMode = 'none';
        // Reset dragOccurred asynchronously so click handlers (which fire after mouseup) can still see it
        if (this.state.dragOccurred)
            setTimeout(() => { this.state.dragOccurred = false; }, 0);
    }
    handleSliderDrag(x) {
        if (!this.isValid)
            return;
        const sliderWidth = this.sliderWidth;
        const canvasWidth = this.config.width - sliderWidth;
        const normalizedX = Math.max(0, Math.min(this.config.width, x));
        const fittedPositions = Math.floor(this.config.width / this.config.positionWidth);
        const visiblePositionsN = Math.floor(this.config.width / this.config.positionWidth);
        const totalSliderRange = this.config.totalPositions - visiblePositionsN;
        const sliderStartPx = Math.max(0, normalizedX - sliderWidth / 2);
        const windowStart = sliderStartPx / (canvasWidth) * (totalSliderRange);
        this.config.windowStartPosition = Math.max(1, Math.min(windowStart, this.config.totalPositions - fittedPositions + 1));
        if (typeof this.config.onPositionChange === 'function')
            this.config.onPositionChange(this.config.currentPosition, this.getWindowRange());
    }
    get headerHeight() {
        return this.config.headerHeight;
    }
    set headerHeight(value) {
        this.config.headerHeight = value;
    }
    handleClick(e) {
        if (this.state.dragOccurred)
            return;
        if (!this.isValid)
            return;
        const absoluteX = e.clientX - this.canvas.getBoundingClientRect().left;
        const absoluteY = e.clientY - this.canvas.getBoundingClientRect().top;
        const { x, y } = this.getCoords(e);
        if (this.handleTrackButtonClick(x, y)) {
            e.preventDefault();
            e.stopPropagation();
            return;
        }
        if (this.isInsideColumnHeaderArea(x, y)) {
            if (this.seqColumn)
                grok.shell.o = this.seqColumn;
            return;
        }
        const sliderTop = this.config.headerHeight - LAYOUT_CONSTANTS.SLIDER_HEIGHT;
        if (y < sliderTop && y >= 0) {
            const cellWidth = this.config.positionWidth;
            const clickedCellIndex = Math.round(x / cellWidth - 0.5);
            const windowStart = this.config.windowStartPosition;
            const clickedPosition = windowStart + clickedCellIndex;
            if (clickedPosition >= 1 && clickedPosition <= this.config.totalPositions) {
                this.config.currentPosition = clickedPosition;
                if (typeof this.config.onPositionChange === 'function')
                    this.config.onPositionChange(this.config.currentPosition, this.getWindowRange());
            }
        }
    }
    getWindowRange() {
        return {
            start: this.config.windowStartPosition,
            end: Math.min(this.config.totalPositions, this.config.windowStartPosition + Math.floor(this.config.width / this.config.positionWidth))
        };
    }
    updateConfig(newConfig) {
        Object.assign(this.config, newConfig);
        this.config.currentPosition = Math.min(this.config.currentPosition, this.config.totalPositions);
        if (typeof this.config.onPositionChange === 'function')
            this.config.onPositionChange(this.config.currentPosition, this.getWindowRange());
    }
    getCurrentPosition() {
        return this.config.currentPosition;
    }
    setCurrentPosition(position) {
        this.config.currentPosition = Math.max(1, Math.min(this.config.totalPositions, position));
        if (typeof this.config.onPositionChange === 'function')
            this.config.onPositionChange(this.config.currentPosition, this.getWindowRange());
    }
    /**
     * ADDED: Public methods for external control
     */
    getHeightThresholds() {
        return {
            BASE: HEIGHT_THRESHOLDS.BASE,
            WITH_TITLE: HEIGHT_THRESHOLDS.WITH_TITLE(),
            WITH_WEBLOGO: HEIGHT_THRESHOLDS.WITH_WEBLOGO(),
            WITH_BOTH: HEIGHT_THRESHOLDS.WITH_BOTH()
        };
    }
}
function between(value, min, max) {
    return value >= min && value <= max;
}
//# sourceMappingURL=sequence-position-scroller.js.map